// ============================================================================
// APPROVAL API TYPES
// ============================================================================
// Types for the Approval Inbox (matches ApprovalRequestDto.cs / approval.vew_approval_request)

/** Single approval request as returned by GET /Approval and /Approval/{id}. */
export interface Approval {
  /** Used as `id` for SyutGrid row keys. */
  id: string; // alias for approvalRequestId
  approvalRequestId: string;

  propertyId: string;
  propertyCode?: string;
  propertyName?: string;

  approvalType: string;        // DISCOUNT | RATE_BELOW_MINIMUM | COMPLIMENTARY | REFUND | ADVANCE_OVERRIDE | OVERDUE_ADVANCE
  referenceType: string;       // Booking
  referenceId: string;
  bookingId?: string | null;
  bookingNumber?: string | null;

  bookingStatusId?: string | null;
  bookingStatusCode?: string | null;
  bookingStatus?: string | null;

  /** Linked booking source columns — used to render the "Booked By" composite cell. */
  bookingSource?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  agentCode?: string | null;
  corporateId?: string | null;
  corporateName?: string | null;
  corporateCode?: string | null;
  otaId?: string | null;
  otaCode?: string | null;
  otaDisplayName?: string | null;
  otaBookingRef?: string | null;
  guestFullName?: string | null;

  /** JSON metadata as stored on tbl_approval_request.meta_data. ADVANCE_OVERRIDE rows carry proposed_advance_due_date. */
  metaData?: string | null;

  requestedRate?: number | null;
  minimumRate?: number | null;
  varianceAmount?: number | null;
  variancePercent?: number | null;
  totalValue?: number | null;
  reasonCode?: string | null;
  justification?: string | null;

  /** Booking context (view 028) — approver decision support. */
  checkInDate?: string | null;
  checkOutDate?: string | null;
  numNights?: number | null;
  totalRooms?: number | null;
  numAdults?: number | null;
  numChildren?: number | null;
  /** "Deluxe × 1, Suite × 2" — aggregated from the booked detail lines. */
  categorySummary?: string | null;
  /** Distinct meal-plan codes on the booking ("CP, MAP"). */
  mealPlanSummary?: string | null;

  requestedBy: string;
  requestedByName?: string | null;
  requestedAt: string; // ISO

  currentLevel: number;

  approvalStatusId: string;
  approvalStatusCode?: string | null; // APPROVAL_PENDING | APPROVED | REJECTED
  approvalStatus?: string | null;
  approvalStatusColor?: string | null;

  approvedBy?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;

  // Post-arrival request settlement (request re-pricing workflow).
  /** What the desk asked for, when the request is a price. */
  requestedAmount?: number | null;
  /** PER_NIGHT or STAY_TOTAL. */
  requestedBasis?: RequestedBasis | null;
  /** REPRICED / NO_CHANGE / DECLINED once settled. */
  resolution?: RequestResolution | null;
  /** The booking total when the item was settled. */
  resolvedTotal?: number | null;
  /** When the Rate Worksheet last re-priced the booking. */
  bookingLastRepricedAt?: string | null;
  /** The booking's total now. */
  bookingCurrentTotal?: number | null;

  statusId: string;
  statusCode?: string | null;
  statusName?: string | null;

  rowVersion: number;
  createdAt: string;
  createdBy?: string | null;

  totalCount: number;
}

/** Filter / pagination for the Approval Inbox list endpoint. */
export interface ApprovalSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  approvalStatusCode?: string;
  /** Multi-select status — when supplied takes priority over approvalStatusCode. */
  approvalStatusCodes?: string[];
  approvalType?: string;
  /** Multi-select type — when supplied takes priority over approvalType. */
  approvalTypes?: string[];
  propertyId?: string;
  bookingId?: string;
  /** Filter to approvals submitted by this user (cashier/operator). */
  requestedById?: string;
  requestedFrom?: string; // ISO
  requestedTo?: string;   // ISO
  /** Inclusive lower bound on approved_at — for auditing decisions over a range. */
  decidedFrom?: string;   // ISO
  decidedTo?: string;     // ISO
  /** True → only pending rows where requested_at < now − 24h. */
  overdueOnly?: boolean;
  /** True (default) → suppress APPROVED / REJECTED so the inbox shows the work queue. */
  hideDecided?: boolean;
  includeInactive?: boolean;
  /**
   * 'mine' (default) → only items routed to the caller's role via the approval
   * hierarchy. 'all' → oversight view, honoured server-side only for senior
   * roles (crs_admin / crs_system).
   */
  scope?: 'mine' | 'all';
}

/** One approval-routing row (which role approves which type). */
export interface ApprovalRoutingRow {
  id: string;
  propertyId?: string | null;
  propertyName?: string | null;
  approvalType: string;
  approvalLevel: number;
  /** Keycloak role CODE (crs_admin, crs_booking_agent, …). */
  approverRole: string;
  approverUserId?: string | null;
  thresholdAmount?: number | null;
  rowVersion: number;
}

export interface UpdateApprovalRoutingRequest {
  approverRole: string;
  rowVersion: number;
}

/**
 * Adds a PROPERTY-SCOPED routing override for a type that already has a global
 * row. Global rows are seeded with the approval type — the server rejects an
 * override for a type with no global fallback, because that would hide a
 * missing seed behind the one property that happens to work.
 */
export interface CreateApprovalRoutingOverrideRequest {
  propertyId: string;
  approvalType: string;
  approverRole: string;
  /** Defaults to 1 server-side. Levels > 1 are dormant. */
  approvalLevel?: number;
}

/** Body for approve / reject endpoints. */
export interface ApprovalDecisionRequest {
  notes?: string;
  rowVersion: number;
  /** Post-arrival items on approve: REPRICED (via the Rate Worksheet) or NO_CHANGE (notes required). */
  resolution?: 'REPRICED' | 'NO_CHANGE';
}

/**
 * An item another module puts in the inbox (GET /Approval/contributed, S3b): today the restaurant's
 * requisitions held by an approval rule. The owning module decides it; the inbox only shows it.
 */
export interface ContributedApproval {
  source: string;
  itemId: string;
  type: string;
  typeLabel: string;
  number: string;
  title: string;
  detail?: string | null;
  amount?: number | null;
  origin?: string | null;
  requestedByName?: string | null;
  requestedAt: string;
  approverRole: string;
}

export type RequestedBasis = 'PER_NIGHT' | 'STAY_TOTAL';
export type RequestResolution = 'REPRICED' | 'NO_CHANGE' | 'DECLINED';

/** Front-office request, arrival change, no-show pricing: settled by re-pricing, not by "approve". */
export const POST_ARRIVAL_TYPES = ['FO_CHANGE_REQUEST', 'ARRIVAL_CHANGE', 'NO_SHOW_PRICING'] as const;
export const isPostArrivalItem = (type?: string | null): boolean =>
  !!type && (POST_ARRIVAL_TYPES as readonly string[]).includes(type);

export const requestedBasisLabel = (b?: string | null): string =>
  b === 'PER_NIGHT' ? 'per night' : b === 'STAY_TOTAL' ? 'stay total' : '';
